/* Tabs: at most `max` (default 5) visible; the selected tab is always visible; the rest go in a
   "More" menu (owner rule: at most 5 tabs). Arrow keys move between visible tabs. */
import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Menu } from './Menu';
import { CapBadge } from './basics';
import { cx } from './cx';

export interface TabSpec { id: string; label: ReactNode; cap?: string; badge?: ReactNode }

export interface TabsProps {
  tabs: TabSpec[];
  value: string;
  onChange: (id: string) => void;
  label: string;
  max?: number;
  bare?: boolean;
  className?: string;
  /** id prefix for aria-controls; the panel should use id `${idPrefix}-panel-${tab.id}`. */
  idPrefix?: string;
}

export function splitTabs(tabs: TabSpec[], value: string, max: number): { visible: TabSpec[]; overflow: TabSpec[] } {
  if (tabs.length <= max) return { visible: tabs, overflow: [] };
  const room = max - 1; // one slot is the "More" menu's neighbour budget: keep max visible tabs in total
  let visible = tabs.slice(0, max);
  const selIdx = tabs.findIndex((t) => t.id === value);
  if (selIdx >= max) visible = [...tabs.slice(0, room), tabs[selIdx]!];
  const ids = new Set(visible.map((t) => t.id));
  return { visible, overflow: tabs.filter((t) => !ids.has(t.id)) };
}

export function Tabs({ tabs, value, onChange, label, max = 5, bare, className, idPrefix }: TabsProps) {
  const { visible, overflow } = splitTabs(tabs, value, max);
  const onKey = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    let target = -1;
    if (d) target = (i + d + visible.length) % visible.length;
    else if (e.key === 'Home') target = 0;
    else if (e.key === 'End') target = visible.length - 1;
    if (target < 0) return;
    e.preventDefault();
    onChange(visible[target]!.id);
    const list = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    list?.[target]?.focus();
  };
  return (
    <div className={cx('tabs', bare && 'bare', className)} role="tablist" aria-label={label}>
      {visible.map((t, i) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          className="tab"
          id={idPrefix ? `${idPrefix}-tab-${t.id}` : undefined}
          aria-controls={idPrefix ? `${idPrefix}-panel-${t.id}` : undefined}
          aria-selected={t.id === value}
          tabIndex={t.id === value ? 0 : -1}
          data-cap={t.cap}
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {t.label}{t.badge}{t.cap && <CapBadge id={t.cap} />}
        </button>
      ))}
      {overflow.length > 0 && (
        <Menu
          label={`More ${label.toLowerCase()}`}
          align="start"
          trigger={(p) => (
            <button type="button" className="tab-more" {...p} ref={p.ref}>
              More <ChevronDown size={14} aria-hidden="true" />
            </button>
          )}
          groups={[{ label: 'More sections', items: overflow.map((t) => ({ id: t.id, label: t.label, cap: t.cap, onSelect: () => onChange(t.id) })) }]}
        />
      )}
    </div>
  );
}
